import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Volume2, Check } from "lucide-react";
import { earnXP } from "../gamification";
import PremiumGate, { isPremium } from "../components/PremiumGate";

const STORIES = [
  {
    id: 1, title: "The New Student", level: "Beginner", emoji: "🏫",
    scenes: [
      { text: "Sarah walked into the classroom on her first day.", question: "Where did Sarah go?", options: ["The park", "The classroom", "The office"], answer: 1 },
      { text: "\"Hello! My name is Sarah,\" she said nervously.", question: "How did Sarah feel?", options: ["Happy", "Nervous", "Angry"], answer: 1 },
      { text: "A boy smiled at her. \"I'm Tom. You can sit next to me.\"", question: "What did Tom offer?", options: ["His lunch", "A seat next to him", "His notebook"], answer: 1 },
      { text: "Sarah smiled and sat down. She felt better already.", question: "How did Sarah feel at the end?", options: ["Worse", "The same", "Better"], answer: 2 },
    ]
  },
  {
    id: 2, title: "At the Coffee Shop", level: "Beginner", emoji: "☕",
    scenes: [
      { text: "\"Good morning! What can I get for you?\" asked the barista.", question: "Who is speaking?", options: ["A teacher", "A barista", "A doctor"], answer: 1 },
      { text: "\"I'd like a large latte with oat milk, please,\" said Emma.", question: "What did Emma order?", options: ["A tea", "A large latte", "A small coffee"], answer: 1 },
      { text: "\"That'll be $4.50. Would you like anything else?\"", question: "How much was the drink?", options: ["$3.50", "$4.50", "$5.50"], answer: 1 },
      { text: "\"No, thank you. Here you go.\" Emma paid and waited for her drink.", question: "Did Emma order anything else?", options: ["Yes", "No", "She left"], answer: 1 },
    ]
  },
  {
    id: 3, title: "The Job Interview", level: "Intermediate", emoji: "💼",
    scenes: [
      { text: "Raj arrived at the office 15 minutes early for his interview.", question: "Was Raj late?", options: ["Yes", "No, he was early", "He was on time"], answer: 1 },
      { text: "\"Tell me about your experience,\" said the manager.", question: "What did the manager ask about?", options: ["His hobbies", "His experience", "His family"], answer: 1 },
      { text: "\"I have three years of experience in software development,\" Raj replied confidently.", question: "How many years of experience does Raj have?", options: ["Two", "Three", "Five"], answer: 1 },
      { text: "\"We'd like to offer you the position!\" the manager said with a smile.", question: "Did Raj get the job?", options: ["No", "Maybe", "Yes"], answer: 2 },
    ]
  },
  {
    id: 4, title: "Lost in the City", level: "Intermediate", emoji: "🗺️",
    scenes: [
      { text: "\"Excuse me, could you tell me how to get to the museum?\" asked Maya.", question: "What is Maya looking for?", options: ["A restaurant", "The museum", "A hotel"], answer: 1 },
      { text: "\"Sure! Go straight for two blocks, then turn left,\" replied the woman.", question: "Which direction should Maya turn?", options: ["Right", "Left", "Straight"], answer: 1 },
      { text: "Maya followed the directions but ended up at a park instead.", question: "Where did Maya end up?", options: ["The museum", "A restaurant", "A park"], answer: 2 },
      { text: "She asked another person and finally found the museum. \"Never give up!\" she thought.", question: "What lesson did Maya learn?", options: ["Don't ask for help", "Never give up", "Stay home"], answer: 1 },
    ]
  },
  {
    id: 5, title: "The Restaurant Review", level: "Advanced", emoji: "🍽️",
    scenes: [
      { text: "\"The ambiance of this restaurant is absolutely delightful,\" wrote the food critic.", question: "What word describes the restaurant's atmosphere?", options: ["Terrible", "Delightful", "Boring"], answer: 1 },
      { text: "\"However, the service was rather slow and the waiter seemed disinterested.\"", question: "How was the service?", options: ["Excellent", "Slow", "Fast"], answer: 1 },
      { text: "\"The pasta, on the other hand, was cooked to perfection — al dente with a rich sauce.\"", question: "How was the food?", options: ["Bad", "Average", "Perfect"], answer: 2 },
      { text: "\"Overall, I would rate this restaurant 3.5 out of 5 stars. Good food, needs better service.\"", question: "What was the final rating?", options: ["2 stars", "3.5 stars", "5 stars"], answer: 1 },
    ]
  },
];

export default function Stories() {
  const [activeStory, setActiveStory] = useState(null);
  const [sceneIndex, setSceneIndex] = useState(0);
  const [selected, setSelected] = useState(null);
  const [correct, setCorrect] = useState(0);
  const [finished, setFinished] = useState(false);

  if (!isPremium()) return <PremiumGate feature="Interactive Stories" />;

  function selectStory(story) { setActiveStory(story); setSceneIndex(0); setSelected(null); setCorrect(0); setFinished(false); }

  function answer(optionIdx) {
    if (selected !== null) return;
    setSelected(optionIdx);
    if (optionIdx === activeStory.scenes[sceneIndex].answer) setCorrect((c) => c + 1);
  }

  function nextScene() {
    if (sceneIndex < activeStory.scenes.length - 1) {
      setSceneIndex((i) => i + 1);
      setSelected(null);
    } else {
      setFinished(true);
      earnXP(20, "lesson");
    }
  }

  function speak(text) {
    if ("speechSynthesis" in window) {
      const u = new SpeechSynthesisUtterance(text); u.rate = 0.85; u.lang = "en-US";
      window.speechSynthesis.speak(u);
    }
  }

  // Story list
  if (!activeStory) {
    return (
      <div className="space-y-4 page-enter relative">
        <div className="blob blob-rose" style={{ width: 320, height: 320, top: -40, right: -40, opacity: 0.18 }} />
        <div className="blob blob-amber" style={{ width: 260, height: 260, top: 280, left: -40, opacity: 0.15 }} />
        <div className="relative">
          <Link to="/dashboard" className="text-gold-700 hover:text-gold-700 link-animate text-sm font-bold">&larr; Back</Link>
          <h1 className="font-display text-4xl md:text-5xl font-normal mt-3"><em>Stories.</em></h1>
          <p className="text-sm text-gray-600 dark:text-gray-300 font-semibold mt-1">Read stories and answer questions to earn XP</p>
        </div>
        <div className="space-y-3 page-enter">
          {STORIES.map((s) => (
            <button key={s.id} onClick={() => selectStory(s)} className="w-full bg-white rounded-xl border border-gray-100 p-5 flex items-center gap-4 hover:border-gold-500 hover:shadow-md transition-all text-left">
              <div className="text-2xl">{s.emoji}</div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900">{s.title}</h3>
                <p className="text-xs text-gray-400">{s.level} — {s.scenes.length} scenes</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-300" />
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Finished
  if (finished) {
    return (
      <div className="space-y-3 page-enter">
        <div className="bg-white rounded-2xl shadow-lg card-hover p-5 text-center">
          <div className="text-3xl mb-4">{correct === activeStory.scenes.length ? "🌟" : "👍"}</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Story Complete!</h2>
          <p className="text-lg text-gray-600 mb-1">{activeStory.title}</p>
          <p className="text-2xl font-bold text-gold-700 my-4">{correct}/{activeStory.scenes.length} correct</p>
          <p className="text-gold-700 font-medium mb-3">+20 XP earned</p>
          <div className="flex gap-3 justify-center">
            <button onClick={() => selectStory(activeStory)} className="bg-ink-900 text-white px-6 py-3 rounded-xl font-semibold hover:bg-ink-900 transition-all">Read Again</button>
            <button onClick={() => setActiveStory(null)} className="border border-gray-200 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-50 transition-all">All Stories</button>
          </div>
        </div>
      </div>
    );
  }

  // Reading scene
  const scene = activeStory.scenes[sceneIndex];
  return (
    <div className="space-y-3 page-enter">
      <div className="flex items-center justify-between">
        <button onClick={() => setActiveStory(null)} className="text-gold-700 hover:text-gold-700 link-animate text-sm">&larr; All Stories</button>
        <span className="text-sm text-gray-400">Scene {sceneIndex + 1}/{activeStory.scenes.length}</span>
      </div>
      <div className="bg-white rounded-2xl shadow-lg card-hover p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="text-2xl">{activeStory.emoji}</div>
          <h2 className="font-bold text-gray-900">{activeStory.title}</h2>
        </div>
        {/* Story text */}
        <div className="bg-gray-50 rounded-xl p-6 mb-3">
          <div className="flex items-start gap-3">
            <p className="text-lg text-gray-800 leading-relaxed flex-1">{scene.text}</p>
            <button onClick={() => speak(scene.text)} className="text-gray-300 hover:text-gold-700 transition-colors flex-shrink-0 mt-1">
              <Volume2 className="w-5 h-5" />
            </button>
          </div>
        </div>
        {/* Question */}
        <h3 className="font-semibold text-gray-900 mb-3">{scene.question}</h3>
        <div className="space-y-2 page-enter">
          {scene.options.map((opt, i) => {
            let style = "border-gray-100 hover:border-gray-300";
            if (selected !== null) {
              if (i === scene.answer) style = "border-sage bg-sage-light text-sage-dark";
              else if (i === selected) style = "border-berry-light bg-berry-light text-berry";
              else style = "border-gray-100 opacity-50";
            } else if (selected === null) {
              style = "border-gray-100 hover:border-gold-500 hover:bg-paper-deep cursor-pointer";
            }
            return (
              <button key={i} onClick={() => answer(i)} className={`w-full p-4 rounded-xl border-2 text-left font-medium text-sm transition-all ${style}`}>
                {opt} {selected !== null && i === scene.answer && <Check className="w-4 h-4 inline ml-2 text-sage-dark" />}
              </button>
            );
          })}
        </div>
        {selected !== null && (
          <button onClick={nextScene} className="mt-3 w-full bg-ink-900 text-white py-3 rounded-xl font-semibold hover:bg-ink-900 transition-all flex items-center justify-center gap-2">
            {sceneIndex < activeStory.scenes.length - 1 ? "Next Scene" : "See Results"} <ChevronRight className="w-5 h-5" />
          </button>
        )}
      </div>
    </div>
  );
}
